import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import ReelPlayer from "@/components/ReelPlayer";
import { Sparkles, Image as ImageIcon, Type, Download, Copy as CopyIcon, Zap, Wand2, Video, Check, Film, Save } from "lucide-react";

const MODULES = [
  { id: "visuel", label: "Visuel produit" },
  { id: "mannequin", label: "Mannequin virtuel" },
  { id: "background", label: "Remplacement de fond" },
  { id: "ugc", label: "UGC simulé" },
  { id: "carrousel", label: "Carrousel storytelling" },
];
const FORMATS = [{ id: "1:1", label: "Carré 1:1" }, { id: "9:16", label: "Story / Reel 9:16" }, { id: "16:9", label: "Paysage 16:9" }];
const TONES = ["percutant", "élégant", "luxe", "fun & jeune", "minimaliste", "urgence / promo"];
const LANGS = ["Français", "Anglais", "Espagnol", "Allemand", "Italien"];
const PLATFORMS = ["Meta", "TikTok", "Google", "Pinterest", "Snapchat"];

export default function Studio() {
  const { user, setUser } = useAuth();
  const [imgForm, setImgForm] = useState({ prompt: "", module: "visuel", format: "1:1", style: "", product_name: "" });
  const [imgLoading, setImgLoading] = useState(false);
  const [imgResult, setImgResult] = useState(null);

  const [copyForm, setCopyForm] = useState({ product: "", tone: "percutant", language: "Français", platform: "Meta", variants: 3 });
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyResult, setCopyResult] = useState(null);

  // --- Video / Reel montage (no AI cost) ---
  const [images, setImages] = useState([]);
  const [reel, setReel] = useState({ selected: [], caption: "", format: "9:16", speed: "normal", title: "" });
  const [savingReel, setSavingReel] = useState(false);
  const [savedReel, setSavedReel] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/studio/history").then((r) => setImages(r.data.assets.filter((a) => a.type === "image"))).catch(() => {});
  }, [imgResult]);

  useEffect(() => {
    const raw = localStorage.getItem("monaga_product");
    if (raw) {
      try {
        const p = JSON.parse(raw);
        setImgForm((f) => ({ ...f, product_name: p.name || "", prompt: p.description ? `Mise en scène publicitaire premium pour : ${p.name}. ${p.description}` : f.prompt }));
        toast.success(`Produit « ${p.name} » chargé`);
      } catch { /* noop */ }
      localStorage.removeItem("monaga_product");
    }
  }, []);

  const toggleReelImg = (id) =>
    setReel((r) => ({ ...r, selected: r.selected.includes(id) ? r.selected.filter((x) => x !== id) : [...r.selected, id] }));

  const reelFrames = reel.selected.map((id) => images.find((a) => a.asset_id === id)?.data_url).filter(Boolean);
  const reelCaptions = reel.caption ? reel.caption.split("\n").map((s) => s.trim()).filter(Boolean) : "";

  const saveReel = async () => {
    if (reel.selected.length === 0) return toast.error("Sélectionnez au moins une image");
    setSavingReel(true);
    try {
      const { data } = await api.post("/studio/save-reel", {
        asset_ids: reel.selected, caption: reel.caption, format: reel.format, speed: reel.speed, title: reel.title || "Montage vidéo",
      });
      setSavedReel(data.asset);
      if (data.credits !== undefined) updateCredits(data.credits);
      toast.success("Montage enregistré ! Vous pouvez l'exporter en MP4.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSavingReel(false); }
  };

  const exportMp4 = async () => {
    if (!savedReel) return;
    setExporting(true);
    toast.info("Génération du fichier MP4… quelques secondes.");
    try {
      const res = await api.get(`/studio/render-reel/${savedReel.asset_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `monaga-reel-${savedReel.asset_id}.mp4`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Vidéo MP4 téléchargée !");
    } catch {
      toast.error("Échec de l'export MP4");
    } finally { setExporting(false); }
  };

  const updateCredits = (c) => setUser((u) => ({ ...u, credits: c }));

  const genImage = async () => {
    if (!imgForm.prompt.trim()) return toast.error("Décrivez la publicité à générer");
    setImgLoading(true); setImgResult(null);
    try {
      const { data } = await api.post("/studio/generate-image", imgForm);
      setImgResult(data.asset); updateCredits(data.credits);
      toast.success("Visuel généré !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de génération");
    } finally { setImgLoading(false); }
  };

  const genCopy = async () => {
    if (!copyForm.product.trim()) return toast.error("Indiquez le produit");
    setCopyLoading(true); setCopyResult(null);
    try {
      const { data } = await api.post("/studio/generate-copy", copyForm);
      setCopyResult(data.asset); updateCredits(data.credits);
      toast.success("Accroches générées !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de génération");
    } finally { setCopyLoading(false); }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = imgResult.data_url; a.download = `monaga-${imgResult.asset_id}.png`; a.click();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Wand2 className="text-primary" /> Studio IA de création</h1>
        <p className="text-muted-foreground">Générez vos visuels et textes publicitaires en quelques secondes.</p>
      </div>

      <Tabs defaultValue="image">
        <TabsList className="rounded-xl flex flex-wrap h-auto w-full justify-start gap-1 p-1.5" data-testid="studio-tabs">
          <TabsTrigger value="image" className="rounded-lg" data-testid="tab-image"><ImageIcon size={16} className="mr-1.5" /> Visuels IA</TabsTrigger>
          <TabsTrigger value="copy" className="rounded-lg" data-testid="tab-copy"><Type size={16} className="mr-1.5" /> Copies publicitaires</TabsTrigger>
          <TabsTrigger value="video" className="rounded-lg" data-testid="tab-video"><Video size={16} className="mr-1.5" /> Vidéos Reels</TabsTrigger>
        </TabsList>

        <TabsContent value="image" className="mt-5">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
              <div>
                <Label>Module de création</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {MODULES.map((m) => (
                    <button key={m.id} onClick={() => setImgForm({ ...imgForm, module: m.id })} className={`px-3 py-2 rounded-xl text-sm border transition-all ${imgForm.module === m.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`} data-testid={`module-${m.id}`}>{m.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="pname">Produit (optionnel)</Label>
                <Input id="pname" value={imgForm.product_name} onChange={(e) => setImgForm({ ...imgForm, product_name: e.target.value })} placeholder="Ex: Sneakers blanches premium" className="mt-1.5 h-11 rounded-xl" data-testid="image-product-name" />
              </div>
              <div>
                <Label htmlFor="prompt">Description de la publicité</Label>
                <Textarea id="prompt" value={imgForm.prompt} onChange={(e) => setImgForm({ ...imgForm, prompt: e.target.value })} placeholder="Décrivez la scène, l'ambiance, les couleurs, le décor…" className="mt-1.5 rounded-xl min-h-[90px]" data-testid="image-prompt" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Format</Label>
                  <Select value={imgForm.format} onValueChange={(v) => setImgForm({ ...imgForm, format: v })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="image-format"><SelectValue /></SelectTrigger>
                    <SelectContent>{FORMATS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="style">Style</Label>
                  <Input id="style" value={imgForm.style} onChange={(e) => setImgForm({ ...imgForm, style: e.target.value })} placeholder="luxe, minimaliste…" className="mt-1.5 h-11 rounded-xl" data-testid="image-style" />
                </div>
              </div>
              <Button onClick={genImage} disabled={imgLoading} className="w-full h-11 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0" data-testid="image-generate-btn">
                {imgLoading ? "Génération IA…" : <><Sparkles size={16} className="mr-1.5" /> Générer · <Zap size={13} className="mx-1" /> 15 crédits</>}
              </Button>
            </div>

            <div className="p-6 rounded-2xl border border-border bg-card flex flex-col">
              <Label className="mb-3">Résultat</Label>
              <div className="flex-1 rounded-2xl border border-dashed border-border bg-background flex items-center justify-center overflow-hidden min-h-[320px]">
                {imgLoading ? (
                  <div className="flex flex-col items-center gap-3"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /><p className="text-sm text-muted-foreground">Création de votre visuel…</p></div>
                ) : imgResult ? (
                  <img src={imgResult.data_url} alt="Visuel généré" className="max-w-full max-h-[420px] object-contain" data-testid="image-result" />
                ) : (
                  <div className="text-center text-muted-foreground p-6"><ImageIcon size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Votre visuel généré apparaîtra ici</p></div>
                )}
              </div>
              {imgResult && (
                <div className="flex items-center justify-between mt-4">
                  <span className="px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-500 text-sm font-semibold">Score IA {imgResult.ai_score}/100</span>
                  <Button variant="outline" onClick={download} className="rounded-xl" data-testid="image-download-btn"><Download size={16} className="mr-1.5" /> Télécharger</Button>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="copy" className="mt-5">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
              <div>
                <Label htmlFor="cproduct">Produit à promouvoir</Label>
                <Input id="cproduct" value={copyForm.product} onChange={(e) => setCopyForm({ ...copyForm, product: e.target.value })} placeholder="Ex: Crème hydratante bio" className="mt-1.5 h-11 rounded-xl" data-testid="copy-product" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Plateforme</Label>
                  <Select value={copyForm.platform} onValueChange={(v) => setCopyForm({ ...copyForm, platform: v })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="copy-platform"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Langue</Label>
                  <Select value={copyForm.language} onValueChange={(v) => setCopyForm({ ...copyForm, language: v })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="copy-language"><SelectValue /></SelectTrigger>
                    <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ton</Label>
                  <Select value={copyForm.tone} onValueChange={(v) => setCopyForm({ ...copyForm, tone: v })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="copy-tone"><SelectValue /></SelectTrigger>
                    <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Variantes (A/B)</Label>
                  <Select value={String(copyForm.variants)} onValueChange={(v) => setCopyForm({ ...copyForm, variants: Number(v) })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="copy-variants"><SelectValue /></SelectTrigger>
                    <SelectContent>{[2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n} variantes</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={genCopy} disabled={copyLoading} className="w-full h-11 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0" data-testid="copy-generate-btn">
                {copyLoading ? "Génération IA…" : <><Sparkles size={16} className="mr-1.5" /> Générer · <Zap size={13} className="mx-1" /> {5 * copyForm.variants} crédits</>}
              </Button>
            </div>

            <div className="space-y-3">
              {copyLoading && <div className="p-10 rounded-2xl border border-border bg-card flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>}
              {copyResult?.variants?.map((v, i) => (
                <div key={i} className="p-5 rounded-2xl border border-border bg-card" data-testid={`copy-variant-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-primary/15 text-primary">Variante {String.fromCharCode(65 + i)}</span>
                    <button onClick={() => { navigator.clipboard.writeText(`${v.headline}\n${v.body}\n${v.cta}`); toast.success("Copié !"); }} className="text-muted-foreground hover:text-foreground"><CopyIcon size={15} /></button>
                  </div>
                  <p className="font-display font-semibold">{v.headline}</p>
                  <p className="text-sm text-muted-foreground mt-1">{v.body}</p>
                  <span className="inline-block mt-3 px-3 py-1 rounded-lg bg-gradient-to-r from-indigo-500 to-cyan-500 text-white text-xs font-medium">{v.cta}</span>
                </div>
              ))}
              {!copyResult && !copyLoading && (
                <div className="p-10 rounded-2xl border border-dashed border-border bg-card text-center text-muted-foreground"><Type size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Vos accroches générées apparaîtront ici</p></div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="video" className="mt-5">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 rounded-xl p-3">
                <Film size={16} className="text-primary shrink-0" /> Montage animé (effets de zoom, transitions & sous-titres) à partir de vos visuels, exportable en MP4.
              </div>
              <div>
                <Label>Sélectionnez vos images ({reel.selected.length})</Label>
                {images.length === 0 ? (
                  <p className="text-sm text-muted-foreground mt-2 p-4 rounded-xl border border-dashed border-border">Générez d'abord des visuels dans l'onglet « Visuels IA ».</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2 mt-1.5 max-h-48 overflow-y-auto">
                    {images.map((a) => {
                      const sel = reel.selected.indexOf(a.asset_id);
                      return (
                        <button key={a.asset_id} onClick={() => toggleReelImg(a.asset_id)} className={`relative aspect-square rounded-lg overflow-hidden border-2 ${sel > -1 ? "border-primary" : "border-transparent"}`} data-testid={`reel-img-${a.asset_id}`}>
                          <img src={a.data_url} alt="" className="w-full h-full object-cover" />
                          {sel > -1 && <div className="absolute inset-0 bg-primary/50 flex items-center justify-center text-white font-bold">{sel + 1}</div>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="reel-title">Titre du montage</Label>
                <Input id="reel-title" value={reel.title} onChange={(e) => setReel({ ...reel, title: e.target.value })} placeholder="Ex: Collection été 2026" className="mt-1.5 h-11 rounded-xl" data-testid="reel-title" />
              </div>
              <div>
                <Label htmlFor="reel-caption">Sous-titres animés (une ligne par image)</Label>
                <Textarea id="reel-caption" value={reel.caption} onChange={(e) => setReel({ ...reel, caption: e.target.value })} placeholder={"Nouveauté\nLivraison offerte\n-30% ce week-end"} className="mt-1.5 rounded-xl min-h-[90px]" data-testid="reel-caption" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Format</Label>
                  <Select value={reel.format} onValueChange={(v) => setReel({ ...reel, format: v })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="reel-format"><SelectValue /></SelectTrigger>
                    <SelectContent>{FORMATS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Rythme</Label>
                  <Select value={reel.speed} onValueChange={(v) => setReel({ ...reel, speed: v })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="reel-speed"><SelectValue /></SelectTrigger>
                    <SelectContent>{[["lent", "Lent"], ["normal", "Normal"], ["rapide", "Rapide"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={saveReel} disabled={savingReel || reel.selected.length === 0} className="w-full h-11 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0" data-testid="reel-save-btn">
                <Save size={16} className="mr-1.5" /> {savingReel ? "Enregistrement…" : <>Générer la vidéo · <Zap size={13} className="mx-1" /> 20 crédits</>}
              </Button>
              {savedReel && (
                <Button onClick={exportMp4} disabled={exporting} variant="outline" className="w-full h-11 rounded-xl" data-testid="reel-export-mp4-btn">
                  <Download size={16} className="mr-1.5" /> {exporting ? "Génération MP4…" : "Télécharger en MP4 (TikTok / Instagram)"}
                </Button>
              )}
            </div>

            <div className="p-6 rounded-2xl border border-border bg-card flex flex-col">
              <Label className="mb-3">Aperçu live du Reel</Label>
              <div className="flex-1 flex items-center justify-center rounded-2xl border border-dashed border-border bg-background min-h-[360px] p-4">
                {reelFrames.length > 0 ? (
                  <ReelPlayer images={reelFrames} caption={reelCaptions} format={reel.format} speed={reel.speed} maxWidth={reel.format === "9:16" ? 240 : reel.format === "1:1" ? 320 : 420} />
                ) : (
                  <div className="text-center text-muted-foreground"><Video size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Sélectionnez des images pour voir l'aperçu animé</p></div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
